import { createAuthClient } from "better-auth/react";
import { usernameClient } from "better-auth/client/plugins";
import { notifyApiError } from "@/lib/api-error";
import { authUrl } from "@/lib/api-base";

const AUTH_TOKEN_KEY = "better-auth-token";
const TAB_AUTH_TOKEN_KEY = "better-auth-tab-token";
const TAB_AUTH_SCOPE_KEY = "better-auth-tab-scope";
const EMPLOYEE_ACCESS_HASH_KEY = "employee-access";

type AuthResultWithToken = {
  data?: {
    token?: string | null;
    user?: unknown;
  } | null;
} | null;

export function isIsolatedEmployeeSession() {
  return typeof window !== "undefined" && window.sessionStorage.getItem(TAB_AUTH_SCOPE_KEY) === "employee";
}

export function getAuthToken() {
  if (isIsolatedEmployeeSession()) {
    return window.sessionStorage.getItem(TAB_AUTH_TOKEN_KEY) ?? "";
  }
  return localStorage.getItem(AUTH_TOKEN_KEY) ?? "";
}

export function setAuthToken(token: string) {
  if (isIsolatedEmployeeSession()) {
    window.sessionStorage.setItem(TAB_AUTH_TOKEN_KEY, token);
    return;
  }
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken() {
  if (isIsolatedEmployeeSession()) {
    window.sessionStorage.removeItem(TAB_AUTH_TOKEN_KEY);
    return;
  }
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function consumeEmployeeAccessTokenFromHash() {
  if (typeof window === "undefined" || !window.location.hash) return false;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get(EMPLOYEE_ACCESS_HASH_KEY)?.trim();
  if (!token) return false;

  window.sessionStorage.setItem(TAB_AUTH_SCOPE_KEY, "employee");
  window.sessionStorage.setItem(TAB_AUTH_TOKEN_KEY, token);
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  return true;
}

export function syncAuthTokenFromResult(result: AuthResultWithToken) {
  const token = result?.data?.token || getAuthToken();

  if (!token) {
    return { token: "", user: result?.data?.user };
  }

  setAuthToken(token);
  return { token, user: result?.data?.user };
}

export const authClient = createAuthClient({
  baseURL: authUrl(),
  plugins: [usernameClient()],
  fetchOptions: {
    auth: {
      type: "Bearer",
      token: getAuthToken
    },
    onResponse: async (context) => {
      const token = context.response.headers.get("set-auth-token");

      if (token) {
        setAuthToken(token);
      }

      if (!context.response.ok) {
        await notifyApiError(context.response);
      }
    }
  }
});
